import { zipSync } from "fflate";
import { Joint, JOINT_TEXTURE_SIZE_MM, JointClass, structures } from "../constants";
import type { Brick, BrickSeries } from "../store/slices/bricksSlice";
import type { WallTileLayout } from "./layout";
import { rasterizeTile, toCanvas } from "./rasterize";
import { computeReliefMaps } from "./relief";

export const EXPORT_PX_PER_MM = 3;
const JPEG_QUALITY = 0.92;

export interface TextureSetRequest {
  layout: WallTileLayout;
  brickSelection: Record<string, number>;
  brickSeries: Record<string, BrickSeries>;
  joint: JointClass;
  jointSize: Record<Joint, number>;
  onProgress(fraction: number): void;
}

export interface TextureSet {
  fileName: string;
  blob: Blob;
}

const loadOriginal = async (src: string) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = src;
  try {
    await img.decode();
  } catch {
    throw new Error(`Could not load image ${src}`);
  }
  return img;
};

const nextFrame = () => new Promise((resolve) => setTimeout(resolve));

const encodeJpeg = async (width: number, height: number, rgba: Uint8ClampedArray) => {
  const blob = await toCanvas(width, height, rgba).convertToBlob({
    type: "image/jpeg",
    quality: JPEG_QUALITY,
  });
  return new Uint8Array(await blob.arrayBuffer());
};

const grayToRgba = (gray: Uint8Array) => {
  const rgba = new Uint8ClampedArray(gray.length * 4);
  for (let i = 0; i < gray.length; i++) {
    rgba[i * 4] = rgba[i * 4 + 1] = rgba[i * 4 + 2] = gray[i];
    rgba[i * 4 + 3] = 255;
  }
  return rgba;
};

const infoText = (
  request: TextureSetRequest,
  size: { widthMm: number; heightMm: number; width: number; height: number; pxPerMm: number }
) => {
  const { layout, brickSelection, brickSeries, joint, jointSize } = request;
  const series = Object.entries(brickSelection)
    .map(([id, rate]) => `  - ${brickSeries[id]?.name ?? id}: ${Math.round(rate * 10) / 10}%`)
    .join("\n");
  return `Brixture texture set

Physical size: ${size.widthMm} x ${size.heightMm} mm (width x height)
Resolution: ${size.width} x ${size.height} px, ${size.pxPerMm.toFixed(3)} px/mm
Tiles seamlessly in both directions.

Maps:
  Albedo     colour without lighting
  Diffuse    colour with occlusion, sun and shadows baked in
  Normal     tangent-space normal map, OpenGL convention (green up)
  Occlusion  ambient occlusion
  Alpha      mask: white is brick, black is joint (not transparency)

Wall configuration:
  Brick series:
${series}
  Brick size: ${layout.brickSize}
  Structure: ${structures[layout.structure].name}
  Joint: ${joint.name}
  Horizontal joint: ${jointSize[Joint.Horizontal]} mm
  Vertical joint: ${jointSize[Joint.Vertical]} mm

Layout seed: ${layout.seed}
Layout algorithm version: ${layout.version}
`;
};

export const exportTextureSet = async (request: TextureSetRequest): Promise<TextureSet> => {
  const { layout, joint, onProgress } = request;

  const bricks = new Map<string, Brick>();
  for (const b of layout.bricks) bricks.set(b.brick.image, b.brick);
  const originals = new Map<string, HTMLImageElement>();
  let loaded = 0;
  onProgress(0);
  const jointImage = await loadOriginal(joint.img);
  await Promise.all(
    [...bricks.keys()].map(async (src) => {
      originals.set(src, await loadOriginal(src));
      onProgress((++loaded / bricks.size) * 0.4);
    })
  );

  await nextFrame();
  const tile = rasterizeTile(layout, {
    pxPerMm: EXPORT_PX_PER_MM,
    jointImage,
    jointTextureSizeMm: JOINT_TEXTURE_SIZE_MM,
    brickImage: (brick) => originals.get(brick.image)!,
  });
  onProgress(0.5);

  await nextFrame();
  const maps = computeReliefMaps(tile);
  onProgress(0.65);

  const widthMm = Math.round(layout.widthMm);
  const heightMm = Math.round(layout.heightMm);
  const base = `Brixture_${layout.brickSize.replace(/[^A-Za-z0-9]+/g, "")}_${widthMm}x${heightMm}mm`;
  const images: [string, Uint8ClampedArray][] = [
    ["Albedo", tile.albedo],
    ["Diffuse", maps.diffuse],
    ["Normal", maps.normal],
    ["Occlusion", maps.occlusion],
    ["Alpha", grayToRgba(tile.mask)],
  ];
  const files: Record<string, Uint8Array> = {};
  for (const [index, [suffix, rgba]] of images.entries()) {
    await nextFrame();
    files[`${base}_${suffix}.jpg`] = await encodeJpeg(tile.width, tile.height, rgba);
    onProgress(0.65 + ((index + 1) / images.length) * 0.3);
  }
  files[`${base}_Info.txt`] = new TextEncoder().encode(
    infoText(request, {
      widthMm,
      heightMm,
      width: tile.width,
      height: tile.height,
      pxPerMm: tile.pxPerMm,
    })
  );

  await nextFrame();
  // JPEGs don't compress further, so store them as they are.
  const zip = zipSync(files, { level: 0 });
  onProgress(1);
  return {
    fileName: `${base}.zip`,
    blob: new Blob([zip as Uint8Array<ArrayBuffer>], { type: "application/zip" }),
  };
};
